// Chip primitive + registry for the agent-first /admin dock.
//
// This file is the ONE canonical per-side source for a chip kind:
// declaring a new kind means appending its literal to `CHIP_KINDS`
// and its registry entry to `REGISTRY` — both live below. The dock
// derives the UI short label, prompt-composition rule, and route
// resolver from the registry entry, so no dock code needs to change
// when a new kind lands. The Python-side mirror lives at
// `libraries/python/phoebe_admin_agent/chat_chip_registry.py`; the
// two files MUST agree on kind literals + rule semantics.

import { type components } from '@libraries/typescript/api_client/generated_api_types';

// Adding a new chip kind is a one-file change on each side:
// append to `CHIP_KINDS` and add a `REGISTRY` entry.
export const CHIP_KINDS = [
    'organization',
    'agent_trace',
    'admin_page',
    'agent_note',
    'conversation',
] as const;
export type ChipKind = (typeof CHIP_KINDS)[number];

// Structured facts captured when a chip is published (PHO-14248):
// the kickoff context carries the facts themselves, not handles the
// agent must dereference later. Sourced from the generated OpenAPI
// types so the TS side can never drift from the Python models in
// `chat_chip_registry.py` (wire contract via
// `admin_agent_chats.chip_set`).
export type OrganizationChipIntegrationFact =
    components['schemas']['OrganizationChipIntegrationFact'];
export type OrganizationChipFacts =
    components['schemas']['OrganizationChipFacts'];
export type AgentTraceChipFacts = components['schemas']['AgentTraceChipFacts'];
export type AdminPageChipFacts = components['schemas']['AdminPageChipFacts'];
export type AgentNoteChipFacts = components['schemas']['AgentNoteChipFacts'];
export type ConversationChipFacts =
    components['schemas']['ConversationChipFacts'];
export type ChipFacts =
    | OrganizationChipFacts
    | AgentTraceChipFacts
    | AdminPageChipFacts
    | AgentNoteChipFacts
    | ConversationChipFacts;

export type Chip = {
    readonly kind: ChipKind;
    // Stable identifier the agent can invoke tools against. String
    // so kinds that reference non-UUID ids (e.g. Logfire trace ids)
    // share the same shape.
    readonly entity_id: string;
    // Presentation label. Rendered as-is inside the chip.
    readonly label: string;
    // Pinned chips survive route changes. Unpinned auto-loaded
    // chips rotate when the route's canonical chip changes.
    readonly pinned: boolean;
    // Records whether the chip was populated by the route's chip
    // source (as opposed to a manual + add). Lets the dock decide
    // whether to replace it on route change.
    readonly auto_loaded: boolean;
    // Structured context captured at publish time by the route page
    // (org lifecycle/integrations/notes/on-call, trace run/cost/cache
    // summary). Null for chips whose source has no fast summary.
    readonly facts?: ChipFacts | null;
};

// Structured handle plus captured facts the agent sees in its
// kickoff context. The `facts` block IS the auto-loaded structured
// context the PHO-14248 spec requires.
export type ChipHandle = {
    readonly kind: ChipKind;
    readonly entity_id: string;
    readonly label: string;
    readonly facts?: ChipFacts | null;
};

// Per-kind registry entry. Frontend-side mirror of
// `libraries/python/phoebe_admin_agent/chat_chip_registry.py`.
// - `shortLabel` is the tiny uppercase tag rendered inside the
//   chip pill (e.g. "org" for organization). Colocated here so the
//   dock never hard-codes kind names.
// - `promptCompositionRule` is a short natural-language directive
//   surfaced next to the chip so the composer prompt has the
//   context the agent needs (e.g. "load this org's integration
//   inventory before answering").
// - `suggestedTasks` become buttons in the chat welcome state.
export type ChatChipSuggestedTask = {
    readonly id: string;
    readonly label: string;
    readonly promptTemplate: string;
    readonly skillPreloadHints: ReadonlyArray<string>;
};

export type ChatChipRegistryEntry = {
    readonly kind: ChipKind;
    readonly shortLabel: string;
    readonly promptCompositionRule: string;
    readonly suggestedTasks: ReadonlyArray<ChatChipSuggestedTask>;
    // Optional route-pattern → chip resolver populated by chip
    // integration tickets. PHO-14248 fills these for organization
    // + agent_trace surfaces.
    readonly resolveChipFromRoute?: (pathname: string) => {
        readonly entity_id: string;
        readonly label: string;
    } | null;
};

const ADMIN_PAGE_ROUTE_PREFIXES = [
    '/admin/analytics/',
    '/admin/agent/pages/',
] as const;

const UUID_ROUTE_SEGMENT_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Extract the trailing `{entity_id}` segment for an exact
// `<routePrefix>/<uuid>` pathname. Deeper sub-routes deliberately do
// not match: the chip covers the entity detail surface, not every
// nested page under it.
function matchUuidDetailRoute(
    pathname: string,
    routePrefix: string,
): string | null {
    if (!pathname.startsWith(`${routePrefix}/`)) {
        return null;
    }
    const segment = pathname.slice(routePrefix.length + 1).replace(/\/$/, '');
    return UUID_ROUTE_SEGMENT_RE.test(segment) ? segment : null;
}

export function shortEntityId(entityId: string): string {
    return entityId.slice(0, 8);
}

// Upgraded label for the agent_trace chip once the trace summary is
// known: `Trace <short-id> · <started ago>`. Compact units keep the
// pill readable; `nowMs` is injectable for deterministic tests.
export function formatAgentTraceChipLabel(
    entityId: string,
    startedAtIso: string,
    nowMs: number = Date.now(),
): string {
    const startedMs = Date.parse(startedAtIso);
    const elapsedS = Math.max(0, Math.floor((nowMs - startedMs) / 1000));
    let ago: string;
    if (elapsedS < 60) {
        ago = 'just now';
    } else if (elapsedS < 3600) {
        ago = `${Math.floor(elapsedS / 60)}m ago`;
    } else if (elapsedS < 86400) {
        ago = `${Math.floor(elapsedS / 3600)}h ago`;
    } else {
        ago = `${Math.floor(elapsedS / 86400)}d ago`;
    }
    return `Trace ${shortEntityId(entityId)} · ${ago}`;
}

// Labels resolved from the pathname alone are fallbacks — the route pages
// publish upgraded labels (org name, trace started-ago) through
// `route_chip_source.ts` as soon as their data lands. Registry content
// (rules, tasks) MUST stay identical to the Python mirror;
// `chat_chips_cross_language_fixture.json` pins all registered kinds.
const REGISTRY: Partial<Record<ChipKind, ChatChipRegistryEntry>> = {
    organization: {
        kind: 'organization',
        shortLabel: 'org',
        promptCompositionRule:
            'This chip is an organization context card. Its facts block ' +
            'carries the structured context captured when the chip was ' +
            'attached: organization id and name, lifecycle stage, ' +
            'integration inventory (WellSky / AxisCare / HHAExchange ' +
            'presence and health), open agent-notes count, and on-call ' +
            'rotation. Start from those facts; use your tools only for ' +
            'deeper detail or fresher data. Treat the entity_id as the ' +
            'organization id for all tool calls.',
        suggestedTasks: [
            {
                id: 'organization.audit-outreach-health.v1',
                label: 'Audit outreach health',
                promptTemplate:
                    'Audit outreach health for {{label}} (organization ' +
                    '{{entity_id}}): review account-health metrics, recent ' +
                    'fill rate, and outreach outcomes, then flag anything ' +
                    'unhealthy with likely causes.',
                skillPreloadHints: ['admin_account_health_metrics'],
            },
            {
                id: 'organization.diff-vs-last-week.v1',
                label: 'Diff vs last week',
                promptTemplate:
                    'Compare this week vs last week for {{label}} ' +
                    '(organization {{entity_id}}): run a trend analysis of ' +
                    'outreach volume, fill rate, and caregiver engagement, ' +
                    'and summarize what changed.',
                skillPreloadHints: [
                    'admin_data_analysis',
                    'admin_account_health_metrics',
                ],
            },
            {
                id: 'organization.check-integration-status.v1',
                label: 'Check integration status',
                promptTemplate:
                    'Check integration status for {{label}} (organization ' +
                    '{{entity_id}}): review source health for its EHR ' +
                    'connectors (WellSky / AxisCare / HHAExchange), recent ' +
                    'connector sync failures, and data freshness.',
                skillPreloadHints: ['admin_source_health_workflow'],
            },
            {
                id: 'organization.summarize-open-issues.v1',
                label: 'Summarize open issues',
                promptTemplate:
                    'Summarize open issues for {{label}} (organization ' +
                    '{{entity_id}}): review prior scratchpad notes and open ' +
                    'agent notes for this organization, and list unresolved ' +
                    'problems by severity.',
                skillPreloadHints: ['admin_agent_scratchpad_workflow'],
            },
        ],
        // No pathname resolver: the contracted organization surface
        // is /admin/accounts/{accountId}, whose route param is the
        // Core account id — not the chip's Phoebe organization id.
        // The accounts page publishes the resolved chip (org id, org
        // name, facts) through `route_chip_source.ts` as soon as its
        // fast summary lands.
    },
    agent_trace: {
        kind: 'agent_trace',
        shortLabel: 'trace',
        promptCompositionRule:
            'This chip is an agent-trace context card. Its facts block ' +
            'carries the structured trace summary captured when the chip ' +
            'was attached: trace id, run id, root span, total tool ' +
            'calls, cost, cache hit rate, succeeded/errored status, and ' +
            'top-level model. Start from those facts; use your tools ' +
            'only to inspect turn-level detail. Treat the entity_id as ' +
            'the agent conversation (trace) id for tool calls; prefer ' +
            'trace data over speculation.',
        suggestedTasks: [
            {
                id: 'agent_trace.explain-what-happened.v1',
                label: 'Explain what happened',
                promptTemplate:
                    'Explain what happened in agent trace {{entity_id}}: ' +
                    'reconstruct the timeline of turns and tool calls, and ' +
                    'summarize the outcome.',
                skillPreloadHints: ['admin_trace_investigation'],
            },
            {
                id: 'agent_trace.why-did-this-fail.v1',
                label: 'Why did this fail?',
                promptTemplate:
                    'Why did agent trace {{entity_id}} fail? Inspect the ' +
                    'failing turn, tool call errors, and retries, and ' +
                    'identify the root cause.',
                skillPreloadHints: ['admin_trace_investigation'],
            },
            {
                id: 'agent_trace.compare-similar-trace.v1',
                label: 'Compare to a similar trace',
                promptTemplate:
                    'Compare agent trace {{entity_id}} to a similar recent ' +
                    'trace: diff the run data for both timelines and tool ' +
                    'call sequences, and call out where behavior diverged.',
                skillPreloadHints: [
                    'admin_trace_investigation',
                    'admin_run_data_workflow',
                ],
            },
            {
                id: 'agent_trace.file-rca-ticket.v1',
                label: 'File an RCA ticket',
                promptTemplate:
                    'File an RCA ticket for agent trace {{entity_id}}: ' +
                    'summarize the incident, root cause, and impact from ' +
                    'the trace timeline, and draft the follow-up issue.',
                skillPreloadHints: ['admin_trace_investigation'],
            },
        ],
        resolveChipFromRoute: (pathname) => {
            const conversationId = matchUuidDetailRoute(
                pathname,
                '/admin/agent-traces',
            );
            if (conversationId === null) {
                return null;
            }
            // Fallback label only: the trace page publishes the
            // `Trace <short-id> · <started ago>` label once its
            // summary fetch lands.
            return {
                entity_id: conversationId,
                label: `Trace ${shortEntityId(conversationId)}`,
            };
        },
    },
    admin_page: {
        kind: 'admin_page',
        shortLabel: 'page',
        promptCompositionRule:
            'This chip is an analytics workspace context card. If entity_id ' +
            'is new-dashboard, use the dashboard authoring skill to create an ' +
            "inactive dashboard draft from the user's request using the " +
            'facts block data_source. Otherwise its ' +
            'facts block carries the exact active version/snapshot pair, the ' +
            'selected version/snapshot pair, data source, query-package ' +
            'digest, data-as-of time, and dashboard control state. Start from ' +
            'those facts, then ' +
            'read the durable Admin page before revising, publishing, rolling ' +
            'back, or refreshing it.',
        suggestedTasks: [
            {
                id: 'revise-admin-page',
                label: 'Revise page',
                promptTemplate:
                    'Read this data page, then help me revise its presentation without changing metric meaning.',
                skillPreloadHints: ['admin_dashboard_page_authoring'],
            },
            {
                id: 'refresh-admin-page',
                label: 'Refresh data',
                promptTemplate:
                    'Read this data page, then refresh its data without changing the presentation version.',
                skillPreloadHints: ['admin_dashboard_page_authoring'],
            },
        ],
        resolveChipFromRoute: (pathname) => {
            const routePrefix = ADMIN_PAGE_ROUTE_PREFIXES.find((prefix) =>
                pathname.startsWith(prefix),
            );
            if (!routePrefix) {
                return null;
            }
            const slug = pathname.slice(routePrefix.length).split('/')[0];
            if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
                return null;
            }
            return {
                entity_id: slug,
                label: `Data page · ${slug}`,
            };
        },
    },
    agent_note: {
        kind: 'agent_note',
        shortLabel: 'note',
        promptCompositionRule:
            'This chip is an agent-note context card. Its facts block ' +
            'carries the structured note summary captured when the chip ' +
            'was attached: note id, organization, pipeline status, ' +
            'source, classified domains, item count, and a content ' +
            'preview. Start from those facts; use your tools for the ' +
            'full note body, its items, and processing history. Treat ' +
            'the entity_id as the agent note id for all tool calls.',
        suggestedTasks: [
            {
                id: 'agent_note.explain-pipeline.v1',
                label: 'Explain this note’s pipeline',
                promptTemplate:
                    'Explain how agent note {{entity_id}} moved through ' +
                    'the notes pipeline: how it was captured, how it ' +
                    'classified, and where each extracted item landed.',
                skillPreloadHints: ['admin_agent_scratchpad_workflow'],
            },
            {
                id: 'agent_note.investigate-source.v1',
                label: 'Investigate the source',
                promptTemplate:
                    'Investigate the source behind agent note ' +
                    '{{entity_id}}: pull the originating trace or ' +
                    'conversation, reconstruct what happened, and judge ' +
                    'whether the note faithfully captures it.',
                skillPreloadHints: ['admin_trace_investigation'],
            },
            {
                id: 'agent_note.find-related-notes.v1',
                label: 'Find related notes',
                promptTemplate:
                    'Find notes related to agent note {{entity_id}}: ' +
                    'search other notes for the same organization and ' +
                    'entities, and summarize recurring themes or ' +
                    'unresolved issues.',
                skillPreloadHints: ['admin_agent_scratchpad_workflow'],
            },
        ],
        resolveChipFromRoute: (pathname) => {
            const noteId = matchUuidDetailRoute(pathname, '/admin/agent-notes');
            if (noteId === null) {
                return null;
            }
            // Fallback label only: the note surfaces publish the
            // upgraded `Note <short-id> · <org name>` label once the
            // note row lands.
            return {
                entity_id: noteId,
                label: `Note ${shortEntityId(noteId)}`,
            };
        },
    },
    conversation: {
        kind: 'conversation',
        shortLabel: 'convo',
        promptCompositionRule:
            'This chip is an SMS-conversation context card. Its facts ' +
            'block carries the linkage captured when the chip was ' +
            'attached: conversation id, organization, channel, and the ' +
            'agent note or rating that referenced it. Use your data ' +
            'tools to read the conversation’s messages before drawing ' +
            'conclusions; treat the entity_id as the conversation id ' +
            'for all tool calls, and quote real messages rather than ' +
            'paraphrasing from memory.',
        suggestedTasks: [
            {
                id: 'conversation.summarize-thread.v1',
                label: 'Summarize this conversation',
                promptTemplate:
                    'Summarize SMS conversation {{entity_id}}: read the ' +
                    'message thread, identify the participants and ' +
                    'intent, and summarize the outcome.',
                skillPreloadHints: ['admin_data_analysis'],
            },
            {
                id: 'conversation.review-agent-handling.v1',
                label: 'Review agent handling',
                promptTemplate:
                    'Review the agent’s handling of SMS conversation ' +
                    '{{entity_id}}: read the thread, evaluate the ' +
                    'agent’s replies for correctness and tone, and flag ' +
                    'anything that should have been handled differently.',
                skillPreloadHints: ['admin_data_analysis'],
            },
        ],
        // No pathname resolver: SMS conversations have no /admin
        // detail route — the org-scoped /conversations view lives
        // outside the dock's mount. Conversation chips attach from
        // the surfaces that reference them (agent-note sources).
    },
};

export function getChatChipRegistryEntry(
    kind: ChipKind,
): ChatChipRegistryEntry | null {
    return REGISTRY[kind] ?? null;
}

export function registerChatChipKind(entry: ChatChipRegistryEntry): void {
    if (REGISTRY[entry.kind]) {
        throw new Error(`Chat chip kind already registered: ${entry.kind}`);
    }
    REGISTRY[entry.kind] = entry;
}

export function listRegisteredChipKinds(): ReadonlyArray<ChipKind> {
    return Object.keys(REGISTRY) as Array<ChipKind>;
}

// Resolve the auto-loaded chip for a given admin route, if any
// registered chip kind covers that route. Called by the dock when
// the URL changes; the returned chip carries `auto_loaded: true`
// and `pinned: false` so it can be rotated out on the next
// navigation.
export function resolveAutoLoadedChipForRoute(pathname: string): {
    readonly kind: ChipKind;
    readonly entity_id: string;
    readonly label: string;
} | null {
    for (const kind of listRegisteredChipKinds()) {
        const entry = REGISTRY[kind];
        const resolved = entry?.resolveChipFromRoute?.(pathname) ?? null;
        if (resolved) {
            return { kind, ...resolved };
        }
    }
    return null;
}

// Expand a suggested task's canonical template against the chip it
// is bound to. `{{label}}` / `{{entity_id}}` are the only
// placeholders — templates are canonical versioned prompts, not
// freeform strings, so the composed first user turn is fully
// determined by (task id, chip). Mirrors
// `expand_suggested_task_prompt` on the Python side.
export function expandSuggestedTaskPrompt(
    task: ChatChipSuggestedTask,
    chip: ChipHandle,
): string {
    return task.promptTemplate
        .replace(/\{\{label\}\}/g, chip.label)
        .replace(/\{\{entity_id\}\}/g, chip.entity_id);
}

// Short label rendered inside the chip pill. Reads from the
// registry entry when one exists; falls back to the raw kind
// literal so unregistered kinds still render sensibly. The dock
// consumes this — no hard-coded kind switch anywhere in the dock.
export function getChipKindShortLabel(kind: ChipKind): string {
    return REGISTRY[kind]?.shortLabel ?? kind;
}
